import { get } from 'lodash';
import { bool, string, func, shape, array, oneOfType } from 'prop-types';
import React from 'react';
import UAParser from 'ua-parser-js';

import { withMeetingContext } from 'Scenes/_root/meetingContext';
import getBrowserDetails from 'Services/helpers/getBrowserDetails';
import components from 'Services/constants/components';
import routesMap from '~/js/routes';
import DevicePermissions from './DevicePermissions';
import EnsureLoggedIn from './EnsureLoggedIn';
import MeetingProtection from './MeetingProtection';
import UnsupportedBrowser from './UnsupportedBrowser';

class Switcher extends React.Component {
  static propTypes = {
    isSupportedBrowser: bool.isRequired,
    consultGuid: string,
    attendeeId: string,
    pageType: string.isRequired,
    modalCloseAll: func.isRequired,
    queryString: shape({
      code: oneOfType([string, array]),
      dataTransferFailed: string,
    }).isRequired,
    meetingPutPatientCode: func.isRequired,
    recordUnsupportedBrowser: func.isRequired,
  };
  static getDerivedStateFromProps(nextProps) {
    let Root;
    if (nextProps.pageType === 'HOME') {
      Root = components.HOME;
    } else if (nextProps.pageType === 'PRACTITIONER_DASHBOARD') {
      Root = components.PRACTITIONER_DASHBOARD;
    } else if (nextProps.pageType === 'PRACTITIONER_LOGIN') {
      Root = components.PRACTITIONER_LOGIN;
    } else if (nextProps.pageType === 'PRACTITIONER_REGISTER') {
      Root = components.PRACTITIONER_REGISTER;
    } else if (nextProps.pageType === 'PATIENT_LOGIN') {
      Root = components.PATIENT_LOGIN;
    } else if (nextProps.pageType === 'PATIENT_MEETING') {
      Root = components.PATIENT_MEETING;
    } else if (nextProps.pageType === 'PATIENT_MEETING_COMPLETE') {
      Root = components.PATIENT_MEETING_COMPLETE;
    }
    if (!Root) {
      Root = components.FOUR_OH_FOUR;
    }
    return {
      Root,
      requiresAuth: get(routesMap, [nextProps.pageType, 'requiresAuth']),
      hasMeetingProtection: get(routesMap, [nextProps.pageType, 'hasMeetingProtection'], false),
      requiresDevicePermission: get(routesMap, [nextProps.pageType, 'requiresDevicePermission'], false),
    };
  }

  constructor(props) {
    super(props);
    this.browserDetails = new UAParser().getResult() || {};

    this.state = {
      Root: components.HOME,
      requiresAuth: false,
      hasMeetingProtection: false,
      requiresDevicePermission: false,
    };
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      Root,
    } = this.state;
    const {
      modalCloseAll,
    } = this.props;
    if (Root !== prevState.Root) {
      this.parseAndFetchQueryStrings();
      modalCloseAll();
    }
  }

  componentDidMount() {
    const { consultGuid: meetingId, attendeeId, recordUnsupportedBrowser } = this.props;
    const browserDetails = getBrowserDetails(this.browserDetails || {});

    if (!this.props.isSupportedBrowser) {
      recordUnsupportedBrowser(meetingId, attendeeId, browserDetails);
    }

    this.parseAndFetchQueryStrings();
  }

  parseAndFetchQueryStrings = () => {
    const {
      queryString: {
        code,
        dataTransferFailed,
      },
      meetingPutPatientCode,
    } = this.props;
    if (!code && !dataTransferFailed) return;
    meetingPutPatientCode(code, dataTransferFailed);
  }

  renderAuthWrappedRoot = () => (
    <EnsureLoggedIn>
      {this.renderRoot()}
    </EnsureLoggedIn>
  );

  renderRoot = () => {
    const {
      Root,
      hasMeetingProtection,
      requiresDevicePermission,
    } = this.state;
    const {
      isSupportedBrowser,
    } = this.props;
    if (!isSupportedBrowser) {
      return (
        <UnsupportedBrowser />
      );
    }
    return (
      <DevicePermissions enabled={requiresDevicePermission}>
        <MeetingProtection enabled={hasMeetingProtection}>
          <Root {...this.props} />
        </MeetingProtection>
      </DevicePermissions>
    );
  }

  render() {
    const {
      requiresAuth,
    } = this.state;
    if (requiresAuth) {
      return this.renderAuthWrappedRoot();
    }
    return this.renderRoot();
  }
}

export default withMeetingContext(['isSupportedBrowser'])(Switcher);
